import { Button, TextInput } from 'components';
import {
  Cell,
  ExpandRow,
  Row,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
} from 'components/FlexTable/Table.styled';
import { TableSpinner } from 'components/TableSpinner';
import { useQueryImportLog } from 'graphql/query/useQueryImportLog';
import { useMemo, useState } from 'react';
import { Box, Flex, Text } from 'ui';

const PAGE_SIZE = 50;
const ITEM_COLUMN_FLEX = 120;
const VALUE_COLUMN_FLEX = 160;

const parseErrorPayload = (raw: string): Record<string, string> => {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, string>;
    }
  } catch {
    // errors payload is not valid JSON, fall back to raw rendering
  }
  return {};
};

type ImportLogExpandRowProps = {
  id: string;
};

export const ImportLogExpandRow = ({ id }: ImportLogExpandRowProps) => {
  const { data, loading } = useQueryImportLog({
    variables: { id: `${id}` },
  });
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [search, setSearch] = useState('');

  const detail = data?.importLog;

  const filteredItems = useMemo(() => {
    if (!detail?.errorItems) return [];
    if (!search) return detail.errorItems;
    const lower = search.toLowerCase();
    return detail.errorItems.filter(
      (item) =>
        item.indexName.toLowerCase().includes(lower) ||
        item.errors.toLowerCase().includes(lower),
    );
  }, [detail?.errorItems, search]);

  const columns = useMemo(() => {
    const firstItem = detail?.errorItems?.[0];
    if (!firstItem) return [];
    return Object.keys(parseErrorPayload(firstItem.errors));
  }, [detail?.errorItems]);

  if (loading) {
    return (
      <ExpandRow>
        <TableSpinner loading />
      </ExpandRow>
    );
  }

  if (!detail) {
    return (
      <ExpandRow>
        <Text p="s20" color="gray60">
          No data available
        </Text>
      </ExpandRow>
    );
  }

  const totalFiltered = filteredItems.length;
  const visibleItems = filteredItems.slice(0, visibleCount);
  const hasMore = visibleCount < totalFiltered;

  return (
    <ExpandRow>
      <Flex flexDirection="column" p="s20" style={{ width: '100%' }}>
        {detail.errorItems.length > 0 ? (
          <>
            <Flex justifyContent="space-between" alignItems="center" mb="s10">
              <Text fontWeight="bold">
                Error Items ({detail.errorItemsCount})
              </Text>
              <Box style={{ width: 260 }}>
                <TextInput
                  placeholder="Search errors..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e?.target.value ?? '');
                    setVisibleCount(PAGE_SIZE);
                  }}
                />
              </Box>
            </Flex>
            {search && (
              <Text color="gray60" mb="s10">
                {`Found ${totalFiltered} of ${detail.errorItems.length}`}
              </Text>
            )}
            <Table>
              <TableHead>
                <TableHeadRow>
                  <TableHeadCell flex={ITEM_COLUMN_FLEX}>Item</TableHeadCell>
                  {columns.length > 0 ? (
                    columns.map((key) => (
                      <TableHeadCell key={key} flex={VALUE_COLUMN_FLEX}>
                        {key}
                      </TableHeadCell>
                    ))
                  ) : (
                    <TableHeadCell flex={VALUE_COLUMN_FLEX}>
                      Errors
                    </TableHeadCell>
                  )}
                </TableHeadRow>
              </TableHead>
              <TableBody>
                {visibleItems.map((item) => {
                  const payload = parseErrorPayload(item.errors);
                  return (
                    <Row key={item.indexName}>
                      <Cell flex={ITEM_COLUMN_FLEX}>
                        <Text fontWeight="bold">{item.indexName}</Text>
                      </Cell>
                      {columns.length > 0 ? (
                        columns.map((key) => (
                          <Cell key={key} flex={VALUE_COLUMN_FLEX}>
                            <Text color="alert50">{payload[key] || '-'}</Text>
                          </Cell>
                        ))
                      ) : (
                        <Cell flex={VALUE_COLUMN_FLEX}>
                          <Text color="alert50">{item.errors}</Text>
                        </Cell>
                      )}
                    </Row>
                  );
                })}
              </TableBody>
            </Table>
            {hasMore && (
              <Flex justifyContent="center" mt="s20">
                <Button
                  variant="text"
                  onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                >
                  {`Show more (${totalFiltered - visibleCount} remaining)`}
                </Button>
              </Flex>
            )}
          </>
        ) : (
          <Text color="gray60">No errors</Text>
        )}
      </Flex>
    </ExpandRow>
  );
};
